import type { ReactNode } from 'react';
import type { PlacementWithLogical } from '@chakra-ui/react';
import { Avatar, Menu, MenuButton, MenuList, Portal } from '@chakra-ui/react';

import CustomIcon from '../icon/CustomIcon';
import { isReactNode } from '../util/react';
import NavigationItem from './NavigationItem';
import type { NavigationMenuItem } from './types';

interface Props<Routes extends string> {
  items: (NavigationMenuItem<Routes> | ReactNode)[];
  renderUserMenuHeader?: ReactNode;
  userName: string;
  menuOffset?: [number, number];
  menuPlacement?: PlacementWithLogical;
}

const DEFAULT_MENU_OFFSET: [number, number] = [0, -10];

const DEFAULT_MENU_PLACEMENT: PlacementWithLogical = 'right-end';

const NavigationUserMenu = <Routes extends string>({
  items,
  userName,
  renderUserMenuHeader,
  menuOffset = DEFAULT_MENU_OFFSET,
  menuPlacement = DEFAULT_MENU_PLACEMENT,
}: Props<Routes>) => {
  return (
    <Menu isLazy placement={menuPlacement} offset={menuOffset}>
      <MenuButton display={{ base: 'none', md: 'inline-flex' }} data-testid='user-menu-button'>
        <Avatar name={userName} />
      </MenuButton>
      <Portal>
        <MenuList
          rootProps={{
            top: 0,
            left: 0,
          }}>
          {renderUserMenuHeader}
          {items.map((item) =>
            isReactNode(item) ? (
              item
            ) : (
              <NavigationItem
                data-testid={`user-menu$${item.href}`}
                key={item.label}
                type='menuItem'
                to={item.href}
                label={item.label}
                icon={<CustomIcon name={item.customIconName} />}
                onClick={item.onClick}
                onMouseEnter={item.onHover}
                onFocus={item.onFocus}
              />
            ),
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default NavigationUserMenu;
